/**
 * 从 PDF 文件中提取第 1 页封面图。
 * Worker 必须与 pdfjs-dist 同版本、同源打包。Vite 本地跨域加载 cdnjs 模块 Worker 会失败。
 */

import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

const MAX_COVER_EDGE = 1600;
const DEFAULT_SCALE = 1.2;
const JPEG_QUALITY = 0.86;

let pdfjsLibCache = null;
let workerConfigured = false;

function warnDev(message, error) {
  if (import.meta.env.DEV) console.warn(message, error);
}

function resolvePdfWorkerSrc() {
  const src = String(pdfWorkerSrc ?? '').trim();
  if (!src || typeof window === 'undefined') return src;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith('/')) return new URL(src, window.location.origin).href;
  return src;
}

async function getPdfJsLib() {
  if (pdfjsLibCache) return pdfjsLibCache;
  if (typeof window === 'undefined') return null;
  try {
    const lib = await import('pdfjs-dist/build/pdf.mjs');
    if (!lib?.getDocument) return null;
    const workerSrc = resolvePdfWorkerSrc();
    if (lib.GlobalWorkerOptions && workerSrc && (!workerConfigured || lib.GlobalWorkerOptions.workerSrc !== workerSrc)) {
      lib.GlobalWorkerOptions.workerSrc = workerSrc;
      workerConfigured = true;
    }
    pdfjsLibCache = lib;
    return lib;
  } catch (err) {
    warnDev('[pdfThumbnail] 动态加载 pdfjs-dist 失败', err);
    return null;
  }
}

function copyPdfBytes(fileOrBuffer) {
  if (typeof Blob !== 'undefined' && fileOrBuffer instanceof Blob) {
    return fileOrBuffer.arrayBuffer().then((buffer) => new Uint8Array(buffer.slice(0)));
  }
  if (fileOrBuffer instanceof ArrayBuffer) {
    return Promise.resolve(new Uint8Array(fileOrBuffer.slice(0)));
  }
  if (ArrayBuffer.isView(fileOrBuffer)) {
    const view = fileOrBuffer;
    return Promise.resolve(new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength)));
  }
  return Promise.resolve(null);
}

function resolveCoverViewport(page, scale) {
  const requested = Number(scale);
  const preferred = Number.isFinite(requested) && requested > 0 ? requested : DEFAULT_SCALE;
  const base = page.getViewport({ scale: 1 });
  const longest = Math.max(base.width, base.height, 1);
  const fit = Math.min(preferred, MAX_COVER_EDGE / longest);
  return page.getViewport({ scale: Math.max(0.15, fit) });
}

function canvasToCoverDataUrl(canvas) {
  try {
    const jpeg = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    if (typeof jpeg === 'string' && jpeg.startsWith('data:image/jpeg')) return jpeg;
  } catch (err) {
    warnDev('[pdfThumbnail] JPEG 导出失败，回退 PNG', err);
  }
  try {
    const png = canvas.toDataURL('image/png');
    return typeof png === 'string' && png.startsWith('data:image/') ? png : '';
  } catch (err) {
    warnDev('[pdfThumbnail] PNG 导出失败', err);
    return '';
  }
}

function buildPdfDocumentInit(overrides = {}) {
  return {
    useSystemFonts: true,
    disableFontFace: false,
    verbosity: 0,
    ...overrides,
  };
}

/**
 * 从 PDF 尾部扫描根 Pages 对象的 /Count（pdf.js 失败时的兜底，仅适用于未压缩 catalog）。
 * @param {Uint8Array} sourceBytes
 * @returns {number}
 */
function scanPdfPhysicalPageCount(sourceBytes) {
  if (!sourceBytes?.byteLength) return 0;
  const tailSize = Math.min(sourceBytes.byteLength, 1024 * 1024);
  const headSize = Math.min(sourceBytes.byteLength, 256 * 1024);
  const chunks = [
    sourceBytes.subarray(0, headSize),
    sourceBytes.subarray(sourceBytes.byteLength - tailSize),
  ];
  let best = 0;
  for (const bytes of chunks) {
    let text = '';
    for (let i = 0; i < bytes.length; i += 1) {
      text += String.fromCharCode(bytes[i]);
    }
    const pagesRe = /\/Type\s*\/Pages/g;
    let match = pagesRe.exec(text);
    while (match) {
      const slice = text.slice(match.index, match.index + 4096);
      const countMatch = slice.match(/\/Count\s+(\d+)/);
      if (countMatch) {
        const count = Number.parseInt(countMatch[1], 10);
        if (Number.isFinite(count) && count > best && count <= 9999) best = count;
      }
      match = pagesRe.exec(text);
    }
  }
  return best;
}

async function readPdfTailBytes(blob, tailSize = 1024 * 1024) {
  if (!blob?.size) return null;
  const start = Math.max(0, blob.size - tailSize);
  const buffer = await blob.slice(start, blob.size).arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * 打开 PDF 文档。File/Blob 优先走 blob URL，避免大文件 data 模式在 Worker 中传输失败。
 * @returns {Promise<{ pdf: import('pdfjs-dist').PDFDocumentProxy, revokeObjectUrl?: () => void }>}
 */
async function openPdfDocument(pdfjsLib, fileOrBuffer) {
  if (typeof Blob !== 'undefined' && fileOrBuffer instanceof Blob) {
    const objectUrl = URL.createObjectURL(fileOrBuffer);
    try {
      const pdf = await pdfjsLib.getDocument(buildPdfDocumentInit({ url: objectUrl })).promise;
      return {
        pdf,
        revokeObjectUrl: () => URL.revokeObjectURL(objectUrl),
      };
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      warnDev('[pdfThumbnail] blob URL 打开 PDF 失败，回退 data 模式', error);
      const sourceBytes = await copyPdfBytes(fileOrBuffer);
      if (!sourceBytes?.byteLength) throw error;
      const pdf = await pdfjsLib.getDocument(buildPdfDocumentInit({ data: sourceBytes.slice() })).promise;
      return { pdf };
    }
  }

  const sourceBytes = await copyPdfBytes(fileOrBuffer);
  if (!sourceBytes?.byteLength) {
    throw new Error('empty pdf source');
  }
  const pdf = await pdfjsLib.getDocument(buildPdfDocumentInit({ data: sourceBytes.slice() })).promise;
  return { pdf };
}

async function destroyPdf(pdf) {
  if (!pdf) return;
  try {
    await pdf.cleanup?.();
  } catch {
    /* ignore */
  }
  try {
    await pdf.destroy?.();
  } catch {
    /* ignore */
  }
}

/**
 * 客户端栅格失败时，允许使用 Python 解析结果里已有的封面。
 * @param {{ clientDataUrl?: string, pythonCoverThumbUrl?: string }} input
 * @returns {{ type: 'data' | 'http' | 'none', url: string }}
 */
function isRasterCoverDataUrl(value) {
  return /^data:image\/(jpeg|jpg|png|webp|gif)/i.test(String(value ?? '').trim());
}

/**
 * 读取 PDF 物理页数（用于导入前预检，不渲染页面）。
 * @param {File|Blob|ArrayBuffer|ArrayBufferView} fileOrBuffer
 * @returns {Promise<number>} 页数；无法读取时返回 0
 */
export async function getPdfPhysicalPageCount(fileOrBuffer) {
  if (!fileOrBuffer) return 0;
  let pdf = null;
  let revokeObjectUrl = null;
  try {
    const pdfjsLib = await getPdfJsLib();
    if (!pdfjsLib) return 0;

    const opened = await openPdfDocument(pdfjsLib, fileOrBuffer);
    pdf = opened.pdf;
    revokeObjectUrl = opened.revokeObjectUrl ?? null;
    const numPages = Number(pdf?.numPages ?? 0);
    if (Number.isFinite(numPages) && numPages > 0) return numPages;
    return 0;
  } catch (error) {
    warnDev('[pdfThumbnail] PDF 页数读取失败', error);
    try {
      if (typeof Blob !== 'undefined' && fileOrBuffer instanceof Blob) {
        const tailBytes = await readPdfTailBytes(fileOrBuffer);
        const scanned = scanPdfPhysicalPageCount(tailBytes);
        if (scanned > 0) return scanned;
      }
      const sourceBytes = await copyPdfBytes(fileOrBuffer);
      const scanned = scanPdfPhysicalPageCount(sourceBytes);
      if (scanned > 0) return scanned;
    } catch (scanError) {
      warnDev('[pdfThumbnail] PDF 页数扫描兜底失败', scanError);
    }
    return 0;
  } finally {
    await destroyPdf(pdf);
    revokeObjectUrl?.();
  }
}

export function resolvePdfCoverForImport({ clientDataUrl, pythonCoverThumbUrl } = {}) {
  const client = String(clientDataUrl ?? '').trim();
  if (isRasterCoverDataUrl(client)) {
    return { type: 'data', url: client };
  }
  const python = String(pythonCoverThumbUrl ?? '').trim();
  if (isRasterCoverDataUrl(python)) {
    return { type: 'data', url: python };
  }
  if (/^https?:\/\//i.test(python)) {
    return { type: 'http', url: python };
  }
  return { type: 'none', url: '' };
}

/**
 * 从 PDF 文件中截取第 1 页为封面图 DataURL
 * @param {File|Blob|ArrayBuffer|ArrayBufferView} fileOrBuffer
 * @param {number} [scale=1.2]
 * @param {{ format?: string, quality?: number, maxDimension?: number }} [options]
 * @returns {Promise<string>} Base64 Data URL
 */
export async function extractPdfFirstPageDataUrl(fileOrBuffer, scale = DEFAULT_SCALE, options = {}) {
  if (!fileOrBuffer) return '';
  let pdf = null;
  let revokeObjectUrl = null;
  try {
    const pdfjsLib = await getPdfJsLib();
    if (!pdfjsLib) return '';

    const opened = await openPdfDocument(pdfjsLib, fileOrBuffer);
    pdf = opened.pdf;
    revokeObjectUrl = opened.revokeObjectUrl ?? null;
    if (!pdf?.numPages) return '';

    const page = await pdf.getPage(1);
    const requested = Number(scale);
    const preferred = Number.isFinite(requested) && requested > 0 ? requested : DEFAULT_SCALE;
    const maxDim = Number(options.maxDimension) > 0 ? Number(options.maxDimension) : MAX_COVER_EDGE;
    const base = page.getViewport({ scale: 1 });
    const longest = Math.max(base.width, base.height, 1);
    const fit = Math.min(preferred, maxDim / longest);
    const viewport = page.getViewport({ scale: Math.max(0.15, fit) });
    if (typeof document === 'undefined') return '';

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return '';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({
      canvas,
      canvasContext: ctx,
      viewport,
    }).promise;

    const format = options.format || 'image/jpeg';
    const quality = options.quality ?? JPEG_QUALITY;
    try {
      const out = canvas.toDataURL(format, quality);
      if (typeof out === 'string' && out.startsWith('data:image/')) return out;
    } catch (err) {
      warnDev('[pdfThumbnail] 指定格式导出失败，回退 JPEG/PNG', err);
    }
    return canvasToCoverDataUrl(canvas);
  } catch (error) {
    warnDev('[pdfThumbnail] PDF 首帧提取异常', error);
    return '';
  } finally {
    await destroyPdf(pdf);
    revokeObjectUrl?.();
  }
}
